﻿import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { administrationPermissions, MemberContext, Permission } from '../contexts/MemberContext';
import { useNavigate } from 'react-router';
import { sitePaths } from '../routes/paths';
import { Contact } from '../api/Common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsLeftRight,
  faListCheck,
  faPenToSquare,
  faPeopleGroup,
  faReply,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import ListIcon from '@mui/icons-material/List';
import { pageAssets } from '../assets/pages';

export function AdministrationMenuButton() {
  const { hasAnyPermission, member, isLoading: isMemberLoading } = useContext(MemberContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setIsAdmin(hasAnyPermission(...administrationPermissions));
  }, [isMemberLoading, member]);

  if (!isAdmin) {
    return <></>;
  }

  return (
    <>
      <Tooltip title="Administration Quick launch">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <AdminPanelSettingsIcon sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Tooltip>
      <AdministrationMenu
        open={open}
        onClose={handleClose}
        member={member}
        anchorEl={anchorEl}
        hasAnyPermission={hasAnyPermission}
      />
    </>
  );
}

const quickLaunchMenuItems = [
  {
    title: pageAssets.activeDamList.title,
    path: sitePaths.adl.admin.root,
    icon: pageAssets.activeDamList.icon,
    permissions: [Permission.AdlViewAdministrator],
  },
  {
    title: pageAssets.calfReturn.title,
    path: sitePaths.calf.return.admin.root,
    icon: pageAssets.calfReturn.icon,
    permissions: [Permission.CalfReturnViewAdministrator],
  },
  {
    title: pageAssets.herd.audit.title,
    path: sitePaths.herd.audit.admin.root,
    icon: pageAssets.herd.audit.icon,
    permissions: [Permission.HerdAuditViewAdministrator],
  },
  {
    title: pageAssets.pedigree.transfers.title,
    path: sitePaths.transfer.admin.root,
    icon: pageAssets.pedigree.transfers.icon,
    permissions: [Permission.PedigreeTransferViewAdministrator],
  },
  {
    title: pageAssets.pedigree.registrations.title,
    path: sitePaths.registration.admin.root,
    icon: pageAssets.pedigree.registrations.icon,
    permissions: [Permission.PedigreeRegistrationViewAdministrator],
  },
  {
    isDivider: true,
  },
  {
    title: pageAssets.members.admin.title,
    path: sitePaths.member.admin.root,
    icon: pageAssets.members.icon,
    permissions: [
      Permission.MemberViewAdministrator,
      Permission.RoleGrantAdministrator,
      Permission.RoleRevokeAdministrator,
      Permission.OfficeViewAdministrator,
    ],
  },
  {
    title: pageAssets.offices.admin.title,
    path: sitePaths.offices.admin.root,
    icon: pageAssets.offices.icon,
    permissions: [Permission.OfficeViewAdministrator],
  },
  {
    title: pageAssets.roles.admin.title,
    path: sitePaths.role.admin.root,
    icon: pageAssets.roles.icon,
    permissions: [Permission.RoleEditAdministrator],
  },
];

export default function AdministrationMenu(props: {
  open: boolean;
  onClose: () => void;
  member?: Contact;
  hasAnyPermission: (...permissions: Permission[]) => boolean;
  anchorEl?: Element | null;
}) {
  const { open, onClose, member, anchorEl, hasAnyPermission } = props;
  const navigate = useNavigate();
  const handleLogout = () => {
    onClose();
    window.location.href = '/auth/logout';
  };

  const handleRoles = () => {
    onClose();
    navigate(sitePaths.role.admin.root);
  };

  const handleItemClick = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleClose = () => {
    onClose();
  };

  const accessibleMenuItems = useMemo(
    () =>
      quickLaunchMenuItems.filter(
        (item) =>
          item.isDivider === true ||
          item.permissions === undefined ||
          hasAnyPermission(...item.permissions)
      ),
    [hasAnyPermission, member]
  );

  return (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem>
        <Stack
          direction="column"
          spacing={0.5}
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant={'subtitle2'} sx={{ textAlign: 'center' }}>
            Quick Launch
          </Typography>
        </Stack>
      </MenuItem>
      <Divider />
      {accessibleMenuItems.map((item, index) =>
        item.isDivider ? (
          index === accessibleMenuItems.length - 1 ? (
            <></>
          ) : (
            <Divider />
          )
        ) : (
          <MenuItem key={`admin-menu-${item.title}`} onClick={() => handleItemClick(item.path!)}>
            <ListItemIcon sx={{ mr: 0 }}>
              {item.icon !== undefined ? item.icon : <></>}
            </ListItemIcon>
            {item.title}
          </MenuItem>
        )
      )}
    </Menu>
  );
}
